/* eslint-disable */
import {Practice} from "../../../../api/lessonsService";
import React from "react";
import ReactMarkdown from "react-markdown";
import {PromptCreationPractice} from "../PromptCreationPractice/PromptCreationPractice";
import {SingleChoiceTestPractice} from "../SingleChoiceTestPractice/SingleChoiceTestPractice";

interface FragmentContentProps {
    content: string;
    practices: Practice[];
    currentFragmentIndex: number;
    lessonId: string;
    courseId: string;
    currentFragmentId: number;
    onPracticeStart: (practiceId: number) => void;
    onPracticeHint: (practiceId: number) => void;
    onPracticeSubmit: (practiceId: number, selectedOption: string) => void;
    onPracticeCompleted: (practiceId: number) => void;
    onMoveNext?: () => void;
}

const FragmentContent: React.FC<FragmentContentProps> = ({
 content, practices,
 currentFragmentIndex,
 lessonId,
 onPracticeStart,
 onPracticeHint,
 onPracticeSubmit,
 onPracticeCompleted,
 courseId,
 currentFragmentId,
 onMoveNext
 // isPracticeCompleted,
}) => {

    if (!practices || practices.length === 0) {
        return (
            <ReactMarkdown
                components={{
                    img: ({ node, ...props }) => (
                        <img
                            {...props}
                            alt={'fragment'}
                            className="fragment-text-image"
                            style={{
                                maxWidth: '100%',
                                height: 'auto',
                                margin: '16px 0',
                                borderRadius: '22px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                        />
                    ),
                    h1: ({ node, ...props }) => (
                        <h1 {...props} style={{ fontSize: '1.8rem', fontWeight: 700, margin: '24px 0 16px 0' }} />
                    ),
                    h2: ({ node, ...props }) => (
                        <h2 {...props} style={{ fontSize: '1.5rem', fontWeight: 600, margin: '20px 0 12px 0' }} />
                    ),
                    h3: ({ node, ...props }) => (
                        <h3 {...props} style={{ fontSize: '1.3rem', fontWeight: 600, margin: '16px 0 8px 0' }} />
                    ),
                    p: ({ node, ...props }) => (
                        <p {...props} style={{ marginBottom: '16px', lineHeight: '1.7' }} />
                    ),
                    ul: ({ node, ...props }) => (
                        <ul {...props} style={{ margin: '16px 0', paddingLeft: '24px' }} />
                    ),
                    ol: ({ node, ...props }) => (
                        <ol {...props} style={{ margin: '16px 0', paddingLeft: '24px' }} />
                    ),
                    li: ({ node, ...props }) => (
                        <li {...props} style={{ marginBottom: '8px' }} />
                    ),
                    blockquote: ({ node, ...props }) => (
                        <blockquote {...props} style={{
                            borderLeft: '4px solid #3b6cff',
                            paddingLeft: '16px',
                            margin: '16px 0',
                            fontStyle: 'italic',
                            color: '#555'
                        }} />
                    ),
                    code: ({ node, ...props }: any) => {
                        const isInline = !node?.children?.[0]?.type;
                        if (isInline) {
                            return (
                                <code {...props} style={{
                                    background: '#f1f3f4',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                                    fontSize: '0.9em'
                                }} />
                            );
                        }
                        return (
                            <pre style={{
                                background: '#f8f9fa',
                                padding: '16px',
                                borderRadius: '8px',
                                overflowX: 'auto',
                                margin: '16px 0'
                            }}>
                <code {...props} style={{ background: 'none', padding: 0 }} />
              </pre>
                        );
                    }
                }}
            >
                {content}
            </ReactMarkdown>
        );
    }

    let processedContent = content;
    const practiceElements: React.ReactElement[] = [];

    // Replace practice markers with practice blocks
    practices.forEach((practice, index) => {
        const marker = `[PRACTICE_${index}]`;

        let practiceComponent: React.ReactElement;

        if (practice.practiceType === 'PROMPT_CREATION') {
            practiceComponent = (
                <PromptCreationPractice
                    courseId={courseId}
                    currentFragmentId={currentFragmentId}
                    key={practice.id}
                    practice={practice}
                    lessonId={parseInt(lessonId || '0')}
                    fragmentIndex={currentFragmentIndex}
                    onStart={() => onPracticeStart(practice.id)}
                    onCompleted={() => onPracticeCompleted(practice.id)}
                    onMoveNext={onMoveNext}
                    // isCompleted={isPracticeCompleted(practice.id)}
                />
            );
        } else if (practice.practiceType === 'SINGLE_CHOICE_TEST') {
            practiceComponent = (
                <SingleChoiceTestPractice
                    courseId={courseId}
                    currentFragmentId={currentFragmentId}
                    key={practice.id}
                    practice={practice}
                    lessonId={parseInt(lessonId || '0')}
                    fragmentIndex={currentFragmentIndex}
                    onHint={() => onPracticeHint(practice.id)}
                    onSubmit={(selectedOption) => onPracticeSubmit(practice.id, selectedOption)}
                    onCompleted={() => onPracticeCompleted(practice.id)}
                    onMoveNext={onMoveNext}
                    // isCompleted={isPracticeCompleted(practice.id)}
                />
            );
        } else {
            practiceComponent = <div key={practice.id}>Unknown practice type</div>;
        }

        // Replace the marker with a unique identifier that we can split on
        const uniqueId = `__PRACTICE_BLOCK_${index}__`;
        processedContent = processedContent.replace(marker, uniqueId);
        practiceElements.push(practiceComponent);
    });

    // Split the content by practice block identifiers
    const parts = processedContent.split(/(__PRACTICE_BLOCK_\d+__)/);

    return (
        <>
            {parts.map((part: string, index: number) => {
                const practiceMatch = part.match(/__PRACTICE_BLOCK_(\d+)__/);
                if (practiceMatch) {
                    const practiceIndex = parseInt(practiceMatch[1]);
                    return practiceElements[practiceIndex];
                }
                return (
                    <ReactMarkdown
                        key={index}
                        components={{
                            img: ({ node, ...props }) => (
                                <img
                                    {...props}
                                    alt={'fragment'}
                                    className="fragment-text-image"
                                    style={{
                                        maxWidth: '100%',
                                        height: 'auto',
                                        margin: '16px 0',
                                        borderRadius: '22px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                />
                            ),
                            h1: ({ node, ...props }) => (
                                <h1 {...props} style={{ fontSize: '1.8rem', fontWeight: 700, margin: '24px 0 16px 0' }} />
                            ),
                            h2: ({ node, ...props }) => (
                                <h2 {...props} style={{ fontSize: '1.5rem', fontWeight: 600, margin: '20px 0 12px 0' }} />
                            ),
                            h3: ({ node, ...props }) => (
                                <h3 {...props} style={{ fontSize: '1.3rem', fontWeight: 600, margin: '16px 0 8px 0' }} />
                            ),
                            p: ({ node, ...props }) => (
                                <p {...props} style={{ marginBottom: '16px', lineHeight: '1.7' }} />
                            ),
                            ul: ({ node, ...props }) => (
                                <ul {...props} style={{ margin: '16px 0', paddingLeft: '24px' }} />
                            ),
                            ol: ({ node, ...props }) => (
                                <ol {...props} style={{ margin: '16px 0', paddingLeft: '24px' }} />
                            ),
                            li: ({ node, ...props }) => (
                                <li {...props} style={{ marginBottom: '8px' }} />
                            ),
                            blockquote: ({ node, ...props }) => (
                                <blockquote {...props} style={{
                                    borderLeft: '4px solid #3b6cff',
                                    paddingLeft: '16px',
                                    margin: '16px 0',
                                    fontStyle: 'italic',
                                    color: '#555'
                                }} />
                            ),
                            code: ({ node, ...props }: any) => {
                                const isInline = !node?.children?.[0]?.type;
                                if (isInline) {
                                    return (
                                        <code {...props} style={{
                                            background: '#f1f3f4',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                                            fontSize: '0.9em'
                                        }} />
                                    );
                                }
                                return (
                                    <pre style={{
                                        background: '#f8f9fa',
                                        padding: '16px',
                                        borderRadius: '8px',
                                        overflowX: 'auto',
                                        margin: '16px 0'
                                    }}>
                    <code {...props} style={{ background: 'none', padding: 0 }} />
                  </pre>
                                );
                            }
                        }}
                    >
                        {part}
                    </ReactMarkdown>
                );
            })}
        </>
    );
};

export default FragmentContent
